/**
 * Brand drill-down — opens from a clickable brand chip on the SearchPage
 * results table. Shows the aggregated catalog footprint a Keepa search
 * sample alone can't reveal: total SKUs, total reviews across the brand,
 * listing age range, size bucket. Never burns Keepa tokens — pure cache read.
 *
 * Why a Dialog (not a Popover): the design system ships Dialog/Card but no
 * Popover primitive. A Dialog is heavier UX-wise but the brand summary is
 * a focused, low-frequency action, so the modal weight is acceptable.
 * Switch to Popover if/when one lands in the design system.
 *
 * Status rendering matches the API route's status field:
 *   fresh        → full aggregates
 *   queued       → in-flight banner + whatever sample data already exists
 *   sample_only  → "sampled" hint with refresh-coming-soon caption
 *   too_large    → "too large for indie outreach" message
 *   failed       → "fetch failed" message; SPA-level retry deferred (C8.x)
 */
import { ExternalLink, ArrowRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { formatBrandReviewsLabel } from '@sourcito/schemas';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../design';
import { useBrandDetails, type BrandDetailsResponse } from '../lib/brands';

interface BrandSummaryDialogProps {
  brand: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function BrandSummaryDialog({ brand, open, onOpenChange }: BrandSummaryDialogProps) {
  // Only fire the query when the dialog is actually open — avoids a flood of
  // background fetches when results render many brand chips.
  const enabledName = open ? brand : null;
  const { data, isLoading, error } = useBrandDetails(enabledName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{data?.name ?? brand}</DialogTitle>
          <DialogDescription>
            Catalog-level view across this brand's full SKU list — surfaces what a 10-ASIN search
            sample can't tell you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && <p className="text-sm text-fg-secondary">Loading brand details…</p>}

          {error && (
            <p className="text-sm text-negative">
              {/* 404 is the unknown-brand case — render it as a friendlier */}
              {/* message than the generic error path. */}
              No catalog data for this brand yet. It may have been enqueued for enrichment — try
              again in a few minutes.
            </p>
          )}

          {data && <BrandDetailsBody data={data} />}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <Link
            to="/brands/$name"
            params={{ name: brand }}
            className="inline-flex items-center gap-1 text-xs text-accent-fg hover:underline"
            onClick={() => onOpenChange(false)}
          >
            View brand <ArrowRight className="size-3" />
          </Link>
          <DialogClose asChild>
            <Button variant="ghost" size="sm">
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BrandDetailsBody({ data }: { data: BrandDetailsResponse }) {
  return (
    <div className="space-y-4">
      <StatusBanner data={data} />

      <Card className="bg-surface-elev">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <Row
            label="Total SKUs"
            value={data.productCount !== null ? data.productCount.toLocaleString() : '—'}
          />
          <Row
            label="Size bucket"
            value={
              data.sizeBucket ? (
                <Badge variant={sizeBadgeVariant(data.sizeBucket)}>{data.sizeBucket}</Badge>
              ) : (
                '—'
              )
            }
          />
          <Row
            label="Total reviews"
            value={formatBrandReviewsLabel({
              totalReviewCount: data.totalReviewCount,
              sampledReviewSum: data.sampledReviewSum,
              sampleSize: data.sampleSize,
              productCount: data.productCount,
            })}
          />
          <Row
            label="Avg reviews / SKU"
            value={data.avgReviewCount !== null ? data.avgReviewCount.toLocaleString() : '—'}
          />
          <Row label="Oldest listing" value={formatRelativeDate(data.oldestListedAt)} />
          <Row label="Newest listing" value={formatRelativeDate(data.newestListedAt)} />
        </dl>
      </Card>

      {data.recentProducts.length > 0 && (
        <a
          href={`/products?brand=${encodeURIComponent(data.name)}`}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-fg hover:underline"
        >
          View all {data.recentProducts.length}
          {data.recentProducts.length === 50 ? '+' : ''} products from {data.name}
          <ExternalLink className="size-3.5" />
        </a>
      )}
    </div>
  );
}

function StatusBanner({ data }: { data: BrandDetailsResponse }) {
  switch (data.status) {
    case 'fresh':
      // The happy path needs no extra chrome — the stats card speaks for itself.
      return null;
    case 'queued':
      return (
        <p className="text-xs text-fg-tertiary italic">
          Full review aggregates queued — refresh in a few minutes. Numbers below may be sampled
          estimates from a prior probe.
        </p>
      );
    case 'sample_only':
      return (
        <p className="text-xs text-fg-tertiary italic">
          Sampled estimate only. A deeper catalog fetch will run in the background next time this
          brand surfaces in a search.
        </p>
      );
    case 'too_large':
      return (
        <p className="text-xs text-warning">
          {data.productCount ?? '500+'} SKUs — too large for indie wholesale outreach. Sample stats
          only; no per-ASIN enumeration.
        </p>
      );
    case 'failed':
      return (
        <p className="text-xs text-negative">
          Brand catalog fetch failed after retries. Sample stats only. Will retry on the next
          refresh window.
        </p>
      );
  }
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="text-fg-primary font-medium">{value}</dd>
    </>
  );
}

function sizeBadgeVariant(
  bucket: 'micro' | 'small' | 'medium' | 'large'
): React.ComponentProps<typeof Badge>['variant'] {
  switch (bucket) {
    case 'micro':
      return 'accent';
    case 'small':
      return 'accent';
    case 'medium':
      return 'neutral';
    case 'large':
      return 'neutral';
  }
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  const ageMs = Date.now() - date.getTime();
  const days = Math.floor(ageMs / 86_400_000);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 24) return `${months} mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}
